import { Route2g } from "@/reducers/routes2gSlice"
import { RouteInfo } from "@/components/maps/RouteView"
import { AvoidType, CarRouteConfig, LoadType } from "@/api/adHocCommands"
import { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { OrderMetaData } from "@/components/maps/AnalyzeOrderVerifyStops"
import { AlternativeRoute, PerLegAlternatives } from "@/lib/planRouteParser"
import { SHARED_ROUTE_PARAM } from "@/lib/appEntryLink"

export interface SavedRouteState {
  // Route planning data (from map planning)
  temporaryRoute?: Route2g
  temporaryRouteInfo?: RouteInfo
  selectedAvoidOptions: AvoidType[]
  selectedLoadType: LoadType[]
  selectedVehicleType: VehicleType
  driverCarConfig?: CarRouteConfig
  // Average fuel consumption (l/100km) entered on top of the vehicle preset
  fuelConsumption?: string
  messageText?: string

  // Alternative routes
  alternativeRoutes?: AlternativeRoute[]
  perLegAlternatives?: PerLegAlternatives[]

  // Document extraction data (from document upload)
  extractedData?: any // The raw extraction response from API
  fileName?: string
  fileUrl?: string
  orderMetaData?: OrderMetaData // The processed/edited metadata
  timestamp: number
}

const KV_STORE_API_PATH = "/kv_store"

type SaveRouteStateParams = {
  temporaryRoute: Route2g | undefined
  temporaryRouteInfo: RouteInfo | undefined
  selectedAvoidOptions: AvoidType[]
  selectedLoadType: LoadType[]
  selectedVehicleType: VehicleType
  driverCarConfig: CarRouteConfig | undefined
  fuelConsumption?: string
  messageText?: string
  alternativeRoutes?: AlternativeRoute[]
  perLegAlternatives?: PerLegAlternatives[]
  extractedData?: any
  fileName?: string
  fileUrl?: string
  orderMetaData?: OrderMetaData
}

interface KVStoreResponse {
  id: string
  payload: any
}

// Implementation
export async function saveRouteState(
  data: SaveRouteStateParams,
): Promise<string> {
  const state: SavedRouteState = {
    timestamp: Date.now(),
    ...data,
  }
  const API_URL =
    window.XMPP === undefined
      ? import.meta.env.REACT_APP_API_PUBLIC_BASE_URL
      : import.meta.env.REACT_APP_API_PRODUCTION_BASE_URL

  try {
    const response = await fetch(`${API_URL}${KV_STORE_API_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        payload: state,
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: KVStoreResponse = await response.json()
    return result.id
  } catch (error) {
    console.error("Failed to save route state:", error)
    throw new Error("Failed to save route state")
  }
}

export async function loadRouteState(
  stateId: string,
): Promise<SavedRouteState | null> {
  try {
    const API_URL = import.meta.env.REACT_APP_API_PUBLIC_BASE_URL

    const response = await fetch(
      `${API_URL}${KV_STORE_API_PATH}/${encodeURIComponent(stateId)}`,
    )

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result: KVStoreResponse = await response.json()
    return result.payload as SavedRouteState
  } catch (error) {
    console.error("Failed to load route state:", error)
    return null
  }
}

export function generateShareUrl(stateId: string): string {
  const baseUrl = !window.location.hostname.includes("app.slickshift.ai")
    ? "https://app.trickswift.eu"
    : "https://app.slickshift.ai"

  return `${baseUrl}/slick-ai/?${SHARED_ROUTE_PARAM}=${stateId}`
}

export function getAPIBaseUrl(): string {
  return window.XMPP
    ? import.meta.env.REACT_APP_API_PRODUCTION_BASE_URL
    : import.meta.env.REACT_APP_API_PUBLIC_BASE_URL
}
